import { Card, Col, Row, Space, Typography } from 'antd'
import { Link } from 'react-router-dom'
import {
  AreaChartOutlined, BarChartOutlined, BuildOutlined, ClockCircleOutlined,
  DollarOutlined, FileDoneOutlined, HeartOutlined, ReloadOutlined,
  TeamOutlined, ThunderboltOutlined, ToolOutlined,
} from '@ant-design/icons'

import PageHeader from '@/shared/components/PageHeader'

const { Title, Text } = Typography

interface ReportTile {
  to: string
  title: string
  description: string
  icon: React.ReactNode
}

const REPORTS: ReportTile[] = [
  {
    to: '/maintenance/reports/machine-downtime',
    title: 'Machine-wise downtime',
    description: 'Per-machine breakdown count, total downtime, MTTR, MTBF, repeat count.',
    icon: <BarChartOutlined />,
  },
  {
    to: '/maintenance/reports/department-breakdown',
    title: 'Department breakdown',
    description: 'Department-level breakdown count, downtime, PM compliance and cost.',
    icon: <BuildOutlined />,
  },
  {
    to: '/maintenance/reports/pm-compliance',
    title: 'PM compliance',
    description: 'Planned vs completed PM per machine, template and frequency. Highlights overdue and deferred PM.',
    icon: <FileDoneOutlined />,
  },
  {
    to: '/maintenance/reports/spare-consumption',
    title: 'Spare consumption',
    description: 'Issued, returned, net consumed quantity per spare with reorder alerts.',
    icon: <ToolOutlined />,
  },
  {
    to: '/maintenance/reports/technician-performance',
    title: 'Technician performance',
    description: 'Assigned, completed, response/repair times, reopens, and completion %.',
    icon: <TeamOutlined />,
  },
  {
    to: '/maintenance/reports/cost',
    title: 'Maintenance cost',
    description: 'Spare + labour + vendor + consumable + downtime cost per WO.',
    icon: <DollarOutlined />,
  },
  {
    to: '/maintenance/reports/repeat-breakdown',
    title: 'Repeat breakdown',
    description: 'Machines + root causes recurring above threshold — chronic issues.',
    icon: <ReloadOutlined />,
  },
  {
    to: '/maintenance/reports/pending-ageing',
    title: 'Pending WO ageing',
    description: 'Bucket-wise ageing of all open work orders. Highlights escalations.',
    icon: <ClockCircleOutlined />,
  },
  {
    to: '/maintenance/reports/utility-performance',
    title: 'Utility performance',
    description: 'Compressor, humidification, DG, panel readings. Flags abnormalities.',
    icon: <ThunderboltOutlined />,
  },
  {
    to: '/maintenance/reports/machine-health',
    title: 'Machine health analytics',
    description: 'Health score, penalty breakdown, suggested action per machine.',
    icon: <HeartOutlined />,
  },
  {
    to: '/maintenance/reports/cost-per-kg-trend',
    title: 'Cost / kg monthly trend',
    description: 'Maintenance cost per kg, by month — backend-calculated, with dept + machine split.',
    icon: <DollarOutlined />,
  },
  {
    to: '/maintenance',
    title: 'Maintenance manager dashboard',
    description: 'Daily operating position — open jobs, PM due, downtime, spare alerts.',
    icon: <AreaChartOutlined />,
  },
  {
    to: '/maintenance/plant-head',
    title: 'Plant head dashboard',
    description: 'High-level cost, downtime, MTTR/MTBF, PM compliance trends.',
    icon: <AreaChartOutlined />,
  },
]

/**
 * Spec §1 — Reports Index. Card grid linking to every Phase 3 screen.
 */
export default function MaintenanceReportsPage() {
  return (
    <div>
      <PageHeader
        title="Maintenance Reports"
        subtitle="Every number is backend-calculated. Filter, drill down, export."
      />

      <Row gutter={[12, 12]}>
        {REPORTS.map((r) => (
          <Col xs={24} md={12} xl={8} key={r.to}>
            <Link to={r.to}>
              <Card hoverable>
                <Space align="start">
                  <div style={{ fontSize: 28 }}>{r.icon}</div>
                  <div>
                    <Title level={5} style={{ marginBottom: 4 }}>{r.title}</Title>
                    <Text type="secondary">{r.description}</Text>
                  </div>
                </Space>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>
    </div>
  )
}
